import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Divider,
  Avatar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
  Chip
} from '@mui/material';
import {
  Person as PersonIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  AdminPanelSettings as AdminIcon,
  AccountCircle as UserIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  Palette as PaletteIcon
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { el } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';

// ✅ NEW: Available profile colors for meeting assignments
const PROFILE_COLORS = [
  { value: '#0015ff', name: 'Μπλε', color: '#0015ff' },
  { value: '#ff00a1', name: 'Ροζ', color: '#ff00a1' },
  { value: '#90fe00', name: 'Πράσινο', color: '#90fe00' },
  { value: '#8400ff', name: 'Μοβ', color: '#8400ff' },
  { value: '#0dff9a', name: 'Τιρκουάζ', color: '#0dff9a' },
  { value: '#000000', name: 'Μαύρο', color: '#000000' }
];

const UserProfile = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const [editMode, setEditMode] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    profile_color: '#0015ff' // ✅ NEW: Profile color
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Fetch current user profile
  const { data: profileResponse, isLoading, error, refetch } = useQuery(
    'userProfile', 
    async () => {
      const axios = (await import('axios')).default;
      const token = localStorage.getItem('token');
      console.log('🔍 Fetching profile with token:', token ? 'EXISTS' : 'NULL');
      
      const response = await axios.get('/api/users/me/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('🔍 Raw profile API response:', response.data);
      return response.data;
    }
  );

  const profile = profileResponse?.data;

  // Update profile mutation
  const updateProfileMutation = useMutation(
    async (updateData) => {
      const axios = (await import('axios')).default;
      const token = localStorage.getItem('token');
      const userId = profile?.id || user?.userId; // ✅ FIX: Use profile.id first
      console.log('🔧 Updating profile for userId:', userId, 'with data:', updateData);
      return axios.put(`/api/users/${userId}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => res.data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('userProfile');
        toast.success('✅ Το προφίλ ενημερώθηκε επιτυχώς');
        setEditMode(false);
      },
      onError: (error) => {
        toast.error(`❌ Σφάλμα: ${error.response?.data?.message || error.message}`);
      }
    }
  );

  // Change password mutation
  const changePasswordMutation = useMutation(
    async ({ passwordInfo, userId }) => {
      const axios = (await import('axios')).default;
      const token = localStorage.getItem('token');
      return axios.put(`/api/users/${userId}/password`, passwordInfo, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => res.data);
    },
    {
      onSuccess: () => {
        toast.success('✅ Ο κωδικός πρόσβασης άλλαξε επιτυχώς');
        setShowPasswordDialog(false);
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      },
      onError: (error) => {
        toast.error(`❌ Σφάλμα: ${error.response?.data?.message || error.message}`);
      }
    }
  );

  // Initialize form data when profile loads
  useEffect(() => {
    if (profile) {
      console.log('🔍 Profile data received:', profile);
      console.log('🔍 Profile firstname:', profile.firstname);
      console.log('🔍 Profile lastname:', profile.lastname);
      console.log('🔍 Profile email:', profile.email);
      console.log('🔍 Profile profile_color:', profile.profile_color);
      
      setFormData({
        firstname: profile.firstname || '',
        lastname: profile.lastname || '',
        email: profile.email || '',
        profile_color: profile.profile_color || '#0015ff' // ✅ NEW: Include profile color
      });
    }
  }, [profile]);

  const handleEditToggle = () => {
    if (editMode) {
      // Cancel edit - reset form data
      setFormData({
        firstname: profile?.firstname || '',
        lastname: profile?.lastname || '',
        email: profile?.email || '',
        profile_color: profile?.profile_color || '#0015ff' // ✅ NEW: Reset profile color too
      });
    }
    setEditMode(!editMode);
  };

  const handleSaveProfile = () => {
    if (!formData.firstname || !formData.lastname || !formData.email) {
      toast.error('❌ Παρακαλώ συμπληρώστε όλα τα πεδία');
      return;
    }
    
    if (!formData.email.includes('@')) {
      toast.error('❌ Παρακαλώ εισάγετε έγκυρο email');
      return;
    }

    updateProfileMutation.mutate(formData);
  };

  const handlePasswordChange = () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error('❌ Παρακαλώ συμπληρώστε όλα τα πεδία');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('❌ Οι νέοι κωδικοί πρόσβασης δεν ταιριάζουν');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('❌ Ο κωδικός πρόσβασης πρέπει να έχει τουλάχιστον 6 χαρακτήρες');
      return;
    }

    changePasswordMutation.mutate({
      passwordInfo: {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      },
      userId: profile?.id || user?.userId // ✅ FIX: Use profile.id first
    });
  };

  const getRoleChip = (role) => {
    const isAdmin = role === 'admin';
    return (
      <Chip
        icon={isAdmin ? <AdminIcon /> : <UserIcon />}
        label={isAdmin ? 'Διαχειριστής' : 'Χρήστης'}
        color={isAdmin ? 'primary' : 'default'}
        size="medium"
      />
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Ποτέ';
    return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: el });
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          ❌ Σφάλμα φόρτωσης προφίλ: {error.response?.data?.message || error.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', fontSize: '1.5rem', color: 'rgba(0, 0, 0, 0.87)' }}>
          Το Προφίλ Μου
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Διαχειρίσου τα στοιχεία του λογαριασμού σου
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Profile Information */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                  <PersonIcon sx={{ mr: 1 }} />
                  Στοιχεία Προφίλ
                </Typography>
                <Button
                  startIcon={editMode ? <CancelIcon /> : <EditIcon />}
                  onClick={handleEditToggle}
                  variant={editMode ? 'outlined' : 'contained'}
                  color={editMode ? 'error' : 'primary'}
                  disabled={updateProfileMutation.isLoading}
                >
                  {editMode ? 'Ακύρωση' : 'Επεξεργασία'}
                </Button>
              </Box>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Όνομα"
                    value={formData.firstname}
                    onChange={(e) => setFormData({ ...formData, firstname: e.target.value })}
                    disabled={!editMode}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BadgeIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Επώνυμο"
                    value={formData.lastname}
                    onChange={(e) => setFormData({ ...formData, lastname: e.target.value })}
                    disabled={!editMode}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BadgeIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    disabled={!editMode}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                {/* ✅ NEW: Profile Color Selection */}
                <Grid item xs={12}>
                  <Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        mb: 2, 
                        display: 'flex', 
                        alignItems: 'center',
                        color: 'text.secondary'
                      }}
                    >
                      <PaletteIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                      Χρώμα Προφίλ (για εμφάνιση στα meetings)
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {PROFILE_COLORS.map((colorOption) => (
                        <Box
                          key={colorOption.value}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            cursor: editMode ? 'pointer' : 'default',
                            opacity: editMode ? 1 : 0.7,
                            p: 1,
                            borderRadius: 1,
                            border: formData.profile_color === colorOption.value ? '2px solid #2088ff' : '2px solid transparent',
                            backgroundColor: formData.profile_color === colorOption.value ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
                            '&:hover': editMode ? { backgroundColor: 'rgba(0,0,0,0.04)' } : {}
                          }}
                          onClick={() => editMode && setFormData({ ...formData, profile_color: colorOption.value })}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor: colorOption.color,
                              borderRadius: '50%',
                              border: '2px solid #fff',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                              mb: 0.5
                            }}
                          />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                            {colorOption.name}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      Το χρώμα θα εμφανίζεται στα badges όταν κάνετε assign meetings
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Όνομα Χρήστη"
                    value={profile?.username || ''}
                    disabled={true}
                    helperText="Το όνομα χρήστη δεν μπορεί να αλλάξει"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              {editMode && (
                <Box mt={3} display="flex" gap={2}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveProfile}
                    disabled={updateProfileMutation.isLoading}
                  >
                    {updateProfileMutation.isLoading ? 'Αποθήκευση...' : 'Αποθήκευση Αλλαγών'}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Security Section */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LockIcon sx={{ mr: 1 }} />
                Ασφάλεια Λογαριασμού
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Button
                variant="outlined"
                startIcon={<LockIcon />}
                onClick={() => setShowPasswordDialog(true)}
              >
                Αλλαγή Κωδικού Πρόσβασης
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Profile Summary */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              {/* ✅ DEBUG SECTION - Temporary for troubleshooting */}
              {process.env.NODE_ENV === 'development' && (
                <Box sx={{ mb: 2, p: 1, bgcolor: 'rgba(255,255,0,0.1)', borderRadius: 1, fontSize: '0.7rem' }}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block' }}>
                    🐛 DEBUG profile: {profile ? 'EXISTS' : 'NULL'}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block' }}>
                    🐛 firstname: "{profile?.firstname || 'EMPTY'}"
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block' }}>
                    🐛 lastname: "{profile?.lastname || 'EMPTY'}"
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block' }}>
                    🐛 username: "{profile?.username || 'EMPTY'}"
                  </Typography>
                </Box>
              )}
              
              <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
                <Avatar 
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    mb: 2, 
                    bgcolor: profile?.profile_color || '#0015ff', // ✅ NEW: Use profile color
                    border: '3px solid #fff',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                  }}
                >
                  {profile?.role === 'admin' ? <AdminIcon sx={{ fontSize: 40 }} /> : <UserIcon sx={{ fontSize: 40 }} />}
                </Avatar>
                
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {(() => {
                    const firstName = profile?.firstname?.trim() || '';
                    const lastName = profile?.lastname?.trim() || '';
                    
                    console.log('🔍 Display name calculation:', { firstName, lastName });
                    
                    if (firstName || lastName) {
                      return `${firstName} ${lastName}`.trim();
                    }
                    return profile?.username || 'Χρήστης';
                  })()}
                </Typography>
                
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  @{profile?.username}
                </Typography>
                
                {getRoleChip(profile?.role)}
                
                <Divider sx={{ width: '100%', my: 2 }} />
                
                <Box textAlign="left" width="100%">
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    <strong>Λογαριασμός δημιουργήθηκε:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {formatDate(profile?.created_at)}
                  </Typography>
                  
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    <strong>Τελευταία σύνδεση:</strong>
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(profile?.last_login)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onClose={() => setShowPasswordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          🔒 Αλλαγή Κωδικού Πρόσβασης
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Τρέχων Κωδικός Πρόσβασης"
                type={showPassword ? 'text' : 'password'}
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Νέος Κωδικός Πρόσβασης"
                type={showPassword ? 'text' : 'password'}
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Επιβεβαίωση Νέου Κωδικού"
                type={showPassword ? 'text' : 'password'}
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                error={passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword}
                helperText={passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword ? 'Οι κωδικοί δεν ταιριάζουν' : ''}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPasswordDialog(false)} startIcon={<CancelIcon />}>
            Ακύρωση
          </Button>
          <Button 
            variant="contained" 
            onClick={handlePasswordChange}
            startIcon={<SaveIcon />}
            disabled={changePasswordMutation.isLoading}
          >
            {changePasswordMutation.isLoading ? 'Αποθήκευση...' : 'Αλλαγή Κωδικού'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserProfile;
